import {GitHubAvatar} from '@github-ui/github-avatar'
import safeStorage from '@github-ui/safe-storage'
import {testIdProps} from '@github-ui/test-id-props'
import {AlertIcon, BookIcon, XIcon} from '@primer/octicons-react'
import {
  ActionList,
  AnchoredOverlay,
  type BetterSystemStyleObject,
  Box,
  Button,
  Heading,
  IconButton,
  Link,
  SelectPanel,
  Truncate,
} from '@primer/react'
import {type ActionListGroupedListProps, type ActionListItemInput, Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {forwardRef, type RefObject, useEffect, useMemo, useRef, useState} from 'react'
import {flushSync} from 'react-dom'

import {isDocset, makeDocsetReference} from '../utils/copilot-chat-helpers'
import type {CopilotChatMode, CopilotChatOrg, Docset} from '../utils/copilot-chat-types'
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import styles from './KnowledgeSelectPanel.module.css'

const KBIconButton = forwardRef<HTMLButtonElement, {open: boolean; setOpen: (v: boolean) => void}>(
  function KBIconButtonInner({open, setOpen, ...buttonProps}, ref) {
    return (
      <IconButton
        ref={ref}
        aria-expanded={open ? true : undefined}
        aria-haspopup
        aria-label="Attach knowledge base"
        aria-labelledby={undefined}
        icon={BookIcon}
        onClick={() => setOpen(!open)}
        variant="invisible"
        className={styles.knowledgeBaseIcon}
        {...buttonProps}
      />
    )
  },
)

interface KnowledgeSelectPanelProps {
  disabledForPreviewModelsProps?: Partial<DisabledForPreviewModelsProps>
  emptyKnowledgeBaseProps?: Partial<EmptyKnowledgeBaseProps>
  open: boolean
  onOpenChange: (val: boolean) => void
  anchorRef: RefObject<HTMLElement>
}

/** `SelectPanel` for filtering and selecting a knowledge base (AKA docset). */
export function KnowledgeSelectPanel({
  disabledForPreviewModelsProps,
  emptyKnowledgeBaseProps,
  open,
  onOpenChange,
  anchorRef,
}: KnowledgeSelectPanelProps) {
  const state = useChatState()
  const manager = useChatManager()
  const safeLocalStorage = safeStorage('localStorage')

  const {knowledgeBases} = state
  const [administratedCopilotEnterpriseOrganizations, setAdministratedCopilotEnterpriseOrganizations] = useState<
    CopilotChatOrg[]
  >([])
  const [orgLoading, setOrgLoading] = useState(true)
  const [filter, setFilter] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  const lastUsedKnowledgeBaseId = safeLocalStorage.getItem('lastUsedKnowledgeBaseId') || ''
  const lastUsedKnowledgeBaseOrg = safeLocalStorage.getItem('lastUsedKnowledgeBaseOrg') || ''
  const {currentReferences} = state

  useEffect(() => {
    if (!open) setFilter('')
  }, [open])

  const filteredItems = useMemo(() => {
    if (filter === '') return knowledgeBases
    return knowledgeBases.filter(item => {
      return item.name.toLowerCase().includes(filter.toLowerCase())
    })
  }, [filter, knowledgeBases])

  const groups = useMemo(() => {
    return filteredItems.reduce<Array<{org: string; docsets: Docset[]}>>((orgs, item) => {
      const entry = orgs.find(org => org.org === item.ownerLogin)
      if (entry) {
        entry.docsets.push(item)
      } else {
        orgs.push({org: item.ownerLogin, docsets: [item]})
      }
      return orgs
    }, [])
  }, [filteredItems])

  const [isOrgLoadingError, setIsOrgLoadingError] = useState(false)

  const loading = orgLoading || state.knowledgeBasesLoading.state === 'pending'
  const isError = isOrgLoadingError || state.knowledgeBasesLoading.state === 'error'

  const hasFetchedDocsetsAndOrgsRef = useRef(false)

  useEffect(() => {
    const fetchDocsetsAndOrgs = async () => {
      setOrgLoading(true)
      await manager.fetchKnowledgeBases()
      const orgResponse = await manager.service.listAdministratedCopilotEnterpriseOrganizations()
      if (orgResponse.ok) {
        setAdministratedCopilotEnterpriseOrganizations(orgResponse.payload || [])
      } else {
        setIsOrgLoadingError(true)
      }
      setOrgLoading(false)
    }

    if (open && !hasFetchedDocsetsAndOrgsRef.current) {
      hasFetchedDocsetsAndOrgsRef.current = true
      void fetchDocsetsAndOrgs()
    }
  }, [open, manager])

  const ssoMessage = useSSOMessage()

  const compareLastUsedKb = (a: Docset, b: Docset) => {
    if (a.id === lastUsedKnowledgeBaseId) return -1
    if (b.id === lastUsedKnowledgeBaseId) return 1
    return 0
  }

  const compareLastUsedKbOrg = (a: {org: string; docsets: Docset[]}, b: {org: string; docsets: Docset[]}) => {
    if (a.org === lastUsedKnowledgeBaseOrg) return -1
    if (b.org === lastUsedKnowledgeBaseOrg) return 1
    return 0
  }

  const toggleDocsetReferences = (docsets: Docset[]) => {
    const referencesToAdd = docsets.filter(
      ref => isDocset(ref) && !currentReferences.some(r => isDocset(r) && r.id === ref.id),
    )
    const referencesToRemove = currentReferences.filter(ref => isDocset(ref) && !docsets.some(d => d.id === ref.id))

    for (const ref of referencesToRemove) {
      manager.removeReference(ref)
    }

    for (const ref of referencesToAdd) {
      manager.addReference(makeDocsetReference(ref), 'docsetMenu')
    }
  }

  if (state.model?.hasLimitedCapabilities) {
    return <DisabledForPreviewModels open={open} setOpen={onOpenChange} {...disabledForPreviewModelsProps} />
  }

  if (!loading && knowledgeBases.length === 0) {
    return (
      <EmptyKnowledgeBase
        administratedCopilotEnterpriseOrganizations={administratedCopilotEnterpriseOrganizations}
        persistentPanelStyles={{
          position: ['fixed', 'fixed', 'absolute'],
          bottom: ['60px', '60px', '0 !important'],
          '@media screen and (max-height: 480px)': {
            bottom: '0 !important',
          },
          top: 'initial !important',
          left: ['18px', '18px', 'calc(50% - 360px) !important'],
          maxHeight: '100%',
        }}
        open={open}
        setOpen={onOpenChange}
        mode={state.mode}
        {...emptyKnowledgeBaseProps}
      />
    )
  }

  const onSelectedChange = (selected: ActionListItemInput[] | ActionListItemInput | undefined) => {
    // this is the entire list of selected docSets in the SelectPanel
    const selectedDocsets: Docset[] = (selected as ActionListItemInput[])
      .map(item => knowledgeBases.find(docset => docset.id === item.id))
      .filter((docset): docset is Docset => docset !== undefined)

    toggleDocsetReferences(selectedDocsets)
  }

  const items: ActionListItemInput[] = filteredItems.sort(compareLastUsedKb).map(docset => {
    return {
      id: docset.id,
      key: docset.id,
      groupId: docset.ownerLogin,
      text: docset.name,
      description: docset.description,
      disabled: !docset.canChat,
      descriptionVariant: 'block',
    }
  })

  const groupMetadata: ActionListGroupedListProps['groupMetadata'] = [...groups]
    .sort(compareLastUsedKbOrg)
    .map(group => ({
      id: group.org,
      key: group.org,
      text: group.org,
      groupId: group.org,
      header: {title: group.org},
    }))

  const selected = knowledgeBases.filter(item => currentReferences.some(ref => isDocset(ref) && ref.id === item.id))

  const multiSelectProps = {
    selected,
    onSelectedChange: (selectedDocsetItems: ActionListItemInput[]) => onSelectedChange(selectedDocsetItems),
  }

  return (
    <div ref={containerRef} {...testIdProps('knowledge-select-panel')}>
      {/* eslint-disable-next-line primer-react/no-system-props */}
      <SelectPanel
        title="Select knowledge bases"
        subtitle="Knowledge bases consolidate content from multiple repositories for an improved chat experience."
        onCancel={() => {
          // eslint-disable-next-line @eslint-react/dom/no-flush-sync
          flushSync(() => onOpenChange(false))
          anchorRef.current?.focus()
        }}
        open={open}
        renderAnchor={null}
        anchorRef={anchorRef}
        onOpenChange={onOpenChange}
        loading={loading}
        items={items}
        onFilterChange={setFilter}
        groupMetadata={groupMetadata}
        notice={
          ssoMessage !== null
            ? {
                text: ssoMessage,
                variant: 'info',
              }
            : undefined
        }
        message={
          isError
            ? {
                variant: 'error',
                title: 'Failed to load knowledge bases',
                body: 'Try again later',
              }
            : groups.length === 0
              ? {
                  variant: 'empty',
                  title: `No knowledge bases found for \`${filter}\``,
                  body: 'Try a different search term',
                }
              : undefined
        }
        {...multiSelectProps}
        variant="modal"
        overlayProps={{
          maxHeight: 'xlarge',
        }}
        width="large"
      />
    </div>
  )
}

interface EmptyKnowledgeBaseProps {
  anchorRef?: RefObject<HTMLButtonElement>
  administratedCopilotEnterpriseOrganizations: CopilotChatOrg[]
  persistentPanelStyles: BetterSystemStyleObject
  open: boolean
  setOpen: (val: boolean) => void
  mode: CopilotChatMode
}

function EmptyKnowledgeBase({
  administratedCopilotEnterpriseOrganizations,
  anchorRef,
  open,
  setOpen,
}: EmptyKnowledgeBaseProps) {
  const ssoMessage = useSSOMessage()
  const anchorProps = anchorRef
    ? {anchorRef, renderAnchor: null}
    : {anchorRef: undefined, renderAnchor: ({...props}) => <KBIconButton open={open} setOpen={setOpen} {...props} />}
  const noAdministratedCopilotEnterpriseOrganizations = administratedCopilotEnterpriseOrganizations?.length === 0
  const middleBoxProps = noAdministratedCopilotEnterpriseOrganizations
    ? {flexGrow: 1, maxHeight: '100%', overflowY: 'auto'}
    : {}
  const subBoxProps = noAdministratedCopilotEnterpriseOrganizations ? {maxHeight: '100%'} : {}
  const boxProps = noAdministratedCopilotEnterpriseOrganizations ? {height: '100%'} : {}

  return (
    <AnchoredOverlay
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      overlayProps={{
        anchorSide: 'outside-bottom',
        sx: {
          width: 'min(100%, 350px)',
        },
      }}
      {...anchorProps}
    >
      <Box
        role="dialog"
        aria-labelledby="knowledge-base-dialog-title"
        sx={{
          ...boxProps,
        }}
        className={styles.KnowledgeSelectPanelContainer}
        {...testIdProps('empty-knowledge-base-picker')}
      >
        <div className={styles.panelHeader}>
          <div className={styles.headerContent}>
            <Heading id="knowledge-base-dialog-title" as="h4" className={styles.panelTitle}>
              Attach a knowledge base
            </Heading>
            {/* eslint-disable-next-line primer-react/a11y-remove-disable-tooltip */}
            <IconButton
              unsafeDisableTooltip
              aria-label="Close"
              icon={XIcon}
              variant="invisible"
              onClick={() => setOpen(false)}
            />
          </div>
        </div>
        <Box
          sx={{
            ...middleBoxProps,
          }}
          className={styles.emptyStateContainer}
        >
          <Octicon icon={BookIcon} size="medium" className={styles.knowledgeBaseIcon} />
          <Box
            sx={{
              ...subBoxProps,
            }}
            className={styles.emptyStateContent}
          >
            <span className={styles.emptyStateMessage}>You don&apos;t have any knowledge bases</span>
            <div className={styles.emptyStateDescription}>
              <p>Knowledge bases consolidate content from multiple repositories for an improved chat experience.</p>
              {noAdministratedCopilotEnterpriseOrganizations && (
                <p className={styles.emptyStateInstructions} {...testIdProps('ask-admin-for-knowledge-base')}>
                  Speak with an organization owner about creating a knowledge base for your organization.
                </p>
              )}
            </div>

            {administratedCopilotEnterpriseOrganizations.length > 0 && (
              <CreateKnowledgeBaseButton
                administratedCopilotEnterpriseOrganizations={administratedCopilotEnterpriseOrganizations}
              />
            )}
          </Box>
        </Box>
        {ssoMessage && (
          <div className={styles.actionButtonsContainer}>
            <SSOFooter message={ssoMessage} />
          </div>
        )}
      </Box>
    </AnchoredOverlay>
  )
}

function CreateKnowledgeBaseButton({
  administratedCopilotEnterpriseOrganizations,
}: {
  administratedCopilotEnterpriseOrganizations: CopilotChatOrg[]
}) {
  if (administratedCopilotEnterpriseOrganizations.length === 1) {
    return (
      <Button
        as="a"
        href={pathToChatSettings(administratedCopilotEnterpriseOrganizations[0]!)}
        className={styles.createButton}
        {...testIdProps('create-knowledge-base-button')}
      >
        Create a knowledge base
      </Button>
    )
  } else {
    return (
      <div className={styles.organizationsSection} {...testIdProps('create-knowledge-base-dropdown')}>
        <Heading as="h3" className={styles.sectionTitle}>
          Create a knowledge base for an organization
        </Heading>
        <ActionList className={styles.organizationsList}>
          {administratedCopilotEnterpriseOrganizations.map(org => (
            <CreateKnowledgeBaseActionRow key={org.id} org={org} />
          ))}
        </ActionList>
      </div>
    )
  }
}

function CreateKnowledgeBaseActionRow({org}: {org: CopilotChatOrg}) {
  return (
    <ActionList.Item>
      {/* It is ok to link directly to chats settings even if the org needs SSO. If the user needs to SSO in they
            will be taken to the SSO page and redirected to the chat settings URL on successful authentication */}
      <Link href={pathToChatSettings(org)} className={styles.organizationLink}>
        <GitHubAvatar
          square
          src={org.avatarUrl}
          alt={org.login}
          aria-hidden="true"
          className={styles.organizationAvatar}
        />
        <Truncate inline title={org.login} className={styles.organizationName}>
          {org.login}
        </Truncate>
      </Link>
    </ActionList.Item>
  )
}

function SSOFooter({message}: {message: string}) {
  if (!message) return null

  return (
    <div data-testid="knowledge-select-panel-sso" className={styles.ssoNotice}>
      {message}
    </div>
  )
}

function useSSOMessage() {
  const state = useChatState()
  const allProtectedOrgs = state.ssoOrganizations.map(org => org.login)
  for (const item of state.knowledgeBases) {
    const protectedOrganizations = item.protectedOrganizations
    for (const org of protectedOrganizations) {
      if (!allProtectedOrgs.includes(org)) {
        allProtectedOrgs.push(org)
      }
    }
  }

  if (!allProtectedOrgs || allProtectedOrgs.length === 0) {
    return null
  } else if (allProtectedOrgs.length === 1) {
    return `Single sign-on to see content from ${allProtectedOrgs[0]!}.`
  } else if (allProtectedOrgs.length === 2) {
    return `Single sign-on to see content from ${allProtectedOrgs[0]!} and ${allProtectedOrgs[1]!}.`
  } else {
    const remainder = allProtectedOrgs.length - 2
    return `Single sign-on to see content from ${allProtectedOrgs[0]!}, ${allProtectedOrgs[1]!}, and ${remainder} organization${
      remainder > 1 ? 's' : ''
    }`
  }
}

function pathToChatSettings(org: CopilotChatOrg) {
  return `/organizations/${org.login}/settings/copilot/chat_settings/new`
}

export interface DisabledForPreviewModelsProps {
  anchorRef?: RefObject<HTMLButtonElement>
  open: boolean
  setOpen: (v: boolean) => void
}

function DisabledForPreviewModels({anchorRef, open, setOpen}: DisabledForPreviewModelsProps) {
  const anchorProps = anchorRef
    ? {anchorRef, renderAnchor: null}
    : {anchorRef: undefined, renderAnchor: ({...props}) => <KBIconButton open={open} setOpen={setOpen} {...props} />}

  return (
    <AnchoredOverlay open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)} {...anchorProps}>
      <div
        className={clsx('m-3 d-flex flex-column flex-items-center text-center', styles.disabledForPreviewModelsPanel)}
      >
        <AlertIcon className="fgColor-attention mb-3" />
        <h3 className="h5 mb-2">Knowledge bases aren&apos;t supported by this model</h3>
        <span className="text-small fgColor-muted">Switch back to the GPT-4o model or start a new conversation</span>
      </div>
    </AnchoredOverlay>
  )
}

try{ KBIconButton.displayName ||= 'KBIconButton' } catch {}
try{ KBIconButtonInner.displayName ||= 'KBIconButtonInner' } catch {}
try{ KnowledgeSelectPanel.displayName ||= 'KnowledgeSelectPanel' } catch {}
try{ EmptyKnowledgeBase.displayName ||= 'EmptyKnowledgeBase' } catch {}
try{ CreateKnowledgeBaseButton.displayName ||= 'CreateKnowledgeBaseButton' } catch {}
try{ CreateKnowledgeBaseActionRow.displayName ||= 'CreateKnowledgeBaseActionRow' } catch {}
try{ SSOFooter.displayName ||= 'SSOFooter' } catch {}
try{ DisabledForPreviewModels.displayName ||= 'DisabledForPreviewModels' } catch {}